import React, { ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ImageUploading, { ImageListType, ImageUploadingPropsType } from 'react-images-uploading';

import { BucketIcon } from 'assets/icons';
import { DeleteIcon, IconWrapper, ImageUploadButton, UploadedImage } from 'common/components';
import {
  ErrorMessage,
  ErrorMessageWrapper,
  ErrorWrapper,
} from 'common/components/ImageUpload/ImageUpload.styled';
import { Flex } from 'components/styled/Flex';

export interface IImageProps extends ImageUploadingPropsType, ImageListType {
  onImgDelete: () => void;
  onImgUpload: (imageLists: ImageListType) => void;
  logo?: string | null;
  maxImageSize?: number;
  uploadButtonWidth?: string;
  uploadButtonHeight?: string;
  uploadButtonBorder?: string;
  uploadButtonBackgroundColor?: string;
  uploadButtonBorderRadius?: string;
  uploadButtonMargin?: string;
  uploadButtonPadding?: string;
  buttonContentBeforeUpload?: ReactNode;
  buttonContentAfterUpload?: ReactNode;
  disableDeleteIcon?: boolean;
  disabled?: boolean;
  height?: string;
}

export const ImageUpload = ({
  onImgDelete,
  onImgUpload,
  logo,
  maxImageSize = 10000000,
  uploadButtonWidth,
  uploadButtonHeight,
  uploadButtonBorder,
  uploadButtonBackgroundColor,
  uploadButtonBorderRadius,
  uploadButtonMargin,
  uploadButtonPadding,
  buttonContentBeforeUpload,
  buttonContentAfterUpload,
  disableDeleteIcon,
  disabled = false,
  height,
  ...rest
}: Partial<IImageProps>) => {
  const { t } = useTranslation();

  const [images, setImages] = useState([]);
  const [logoState, setLogoState] = useState<string | null>(logo!);

  const onChangeImageHandler = useCallback(
    (imageList: ImageListType | ImageListType[], addUpdateIndex: number[] | undefined) => {
      if (disabled) return;

      const imageSize = imageList.map((image: any) => image.file.size);
      const isImageTooBig = imageSize.filter((num: number) => num >= maxImageSize).length;

      setImages(imageList as never[]);
      if (isImageTooBig > 0) return;

      onImgUpload && onImgUpload(imageList);
    },
    [onImgUpload, disabled],
  );

  return (
    <Flex height={height ?? '100%'} width="100%">
      <ImageUploading onChange={onChangeImageHandler} value={images} {...rest}>
        {({ imageList, onImageUpload, onImageRemove, dragProps, isDragging }) => {
          return (
            <Flex width="100%">
              {imageList.length < 1 && (
                <ImageUploadButton
                  type="button"
                  width={uploadButtonWidth}
                  height={uploadButtonHeight}
                  backgroundColor={uploadButtonBackgroundColor}
                  border={uploadButtonBorder}
                  borderRadius={uploadButtonBorderRadius}
                  margin={uploadButtonMargin}
                  padding={uploadButtonPadding || (!logoState ? '6rem' : '2rem')}
                  onClick={!disabled ? onImageUpload : undefined}
                  isDragging={!disabled && isDragging}
                  style={disabled ? { cursor: 'not-allowed' } : undefined}
                  {...(disabled ? {} : dragProps)}
                >
                  {!logoState && buttonContentBeforeUpload}
                  {logoState && buttonContentAfterUpload}
                  {logoState && !buttonContentAfterUpload && (
                    <UploadedImage key={0} src={logoState} />
                  )}

                  {!disableDeleteIcon && !disabled && (
                    <DeleteIcon top="2.3rem" right="1.8rem">
                      <IconWrapper
                        onClick={() => {
                          setLogoState(null);

                          return onImgDelete && onImgDelete();
                        }}
                      >
                        <BucketIcon />
                      </IconWrapper>
                    </DeleteIcon>
                  )}
                </ImageUploadButton>
              )}

              {imageList.map((image: any, index: number) => {
                return (
                  <ImageUploadButton
                    type="button"
                    key={index}
                    width={uploadButtonWidth}
                    height={uploadButtonHeight}
                    margin={uploadButtonMargin}
                    backgroundColor={uploadButtonBackgroundColor}
                    padding={image.file.size > maxImageSize ? '5rem' : uploadButtonPadding}
                    onClick={!disabled ? onImageUpload : undefined}
                    isDragging={!disabled && isDragging}
                    style={disabled ? { cursor: 'not-allowed' } : undefined}
                    {...(disabled ? {} : dragProps)}
                  >
                    {image.file.size > maxImageSize ? (
                      <ErrorWrapper>
                        <ErrorMessageWrapper>
                          <ErrorMessage>{t('Image is too large for upload')}</ErrorMessage>
                        </ErrorMessageWrapper>
                      </ErrorWrapper>
                    ) : !!buttonContentAfterUpload ? (
                      buttonContentAfterUpload
                    ) : (
                      <UploadedImage key={index} src={image.dataURL} />
                    )}

                    {!disableDeleteIcon && !disabled && (
                      <DeleteIcon>
                        <IconWrapper
                          onClick={() => {
                            onImageRemove(index);
                            onImgDelete && onImgDelete();
                          }}
                        >
                          <BucketIcon />
                        </IconWrapper>
                      </DeleteIcon>
                    )}
                  </ImageUploadButton>
                );
              })}
            </Flex>
          );
        }}
      </ImageUploading>
    </Flex>
  );
};
